import { Brain, Info } from 'lucide-react';
import { PRICED_MODELS, type NoticeModel } from './models';
import { useLanguage } from '../../contexts/LanguageContext';
import { useStableText } from '../../components/StableText';

// Token counts rounded to the nearest thousand for the admin table, e.g.
// 1 234 -> "1K", 1 500 -> "2K", 0 -> "0K".
const toK = (n: number) => `${Math.round(n / 1000).toLocaleString()}K`;

// Per-model run economics for the NoticeIA categorize feature: cost, average
// time, number of calls and token usage (input / output / thinking). These are
// totals across the whole api_usage history, not just the current document. The
// table is read-only and lives in the standalone Pricing & Quotas tab.
export function ModelStatsTable({
  costs,
  times,
  calls,
  tokens,
  pricing,
  models = PRICED_MODELS,
}: {
  costs: Record<string, number>;
  times: Record<string, number>;
  calls: Record<string, number>;
  tokens: Record<string, { input: number; output: number; thinking: number }>;
  pricing: Record<string, { input: number; output: number }>;
  // Which models to show as rows (defaults to the user-facing run models).
  models?: NoticeModel[];
}) {
  const { t } = useLanguage();
  const st = useStableText();

  // A Total row only earns its place when there is more than one model to add up —
  // a one-row table's total is just the row again. Cost, calls and tokens sum; the
  // average time is a call-weighted mean (averaging averages would be wrong), the
  // same recombination mergePhases uses when folding phase buckets together.
  const showTotal = models.length > 1;
  const total = models.reduce(
    (acc, m) => {
      const c = calls[m.id] ?? 0;
      const tk = tokens[m.id];
      return {
        cost: acc.cost + (costs[m.id] ?? 0),
        calls: acc.calls + c,
        weightedTime: acc.weightedTime + (times[m.id] ?? 0) * c,
        input: acc.input + (tk?.input ?? 0),
        output: acc.output + (tk?.output ?? 0),
        thinking: acc.thinking + (tk?.thinking ?? 0),
      };
    },
    { cost: 0, calls: 0, weightedTime: 0, input: 0, output: 0, thinking: 0 },
  );
  const avgTime = total.calls > 0 ? total.weightedTime / total.calls : 0;

  // No overflow-hidden on the wrapper: the per-row model/pricing tooltip pops above
  // its row and must be free to extend past the table's top edge. The table's inner
  // grid lines stop short of the rounded corners (last cell/row borders are stripped),
  // so the corners stay clean without any clipping.
  return (
    <div className="mx-auto max-w-3xl rounded-xl border-2 border-slate-300 bg-white shadow-sm">
      <table className="w-full text-center text-sm">
        <thead>
          <tr className="border-b-2 border-slate-300 text-xs uppercase tracking-wide text-slate-900 [&>th]:border-r-2 [&>th]:border-slate-300 [&>th:last-child]:border-r-0">
            <th className="w-56 px-4 py-2 font-medium">{st('notice.cat.model')}</th>
            <th className="px-4 py-2 font-medium">{st('notice.cat.cost')}</th>
            <th className="px-4 py-2 font-medium">{st('notice.cat.time')}</th>
            <th className="px-4 py-2 font-medium">{st('notice.cat.calls')}</th>
            <th className="px-4 py-2 font-medium">
              {st('notice.cat.tokens')}
              <br />
              {st('notice.cat.tokensUnits')}
            </th>
          </tr>
        </thead>
        <tbody>
          {models.map((m) => {
            const price = pricing[m.id];
            return (
              <tr
                key={m.id}
                className="border-b-2 border-slate-300 last:border-0 [&>td]:border-r-2 [&>td]:border-slate-300 [&>td:last-child]:border-r-0"
              >
                <td className="w-56 px-4 py-2.5 text-center font-semibold text-slate-900">
                  <span className="inline-flex items-center justify-center gap-1.5">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: m.color }}
                      aria-hidden
                    />
                    <span>{m.label}</span>
                    <span className="group relative inline-flex">
                      <Info className="h-3.5 w-3.5 text-slate-400 transition-colors group-hover:text-blue-600" />
                      <span
                        role="tooltip"
                        className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-max -translate-x-1/2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs font-normal leading-relaxed text-slate-700 opacity-0 shadow-lg transition-opacity group-hover:opacity-100"
                      >
                        {t('notice.cat.modelId')}: <span className="font-mono">{m.id}</span>
                        {price && (
                          <>
                            <br />
                            {t('notice.cat.priceIn')}: ${price.input.toFixed(2)} / 1M
                            <br />
                            {t('notice.cat.priceOut')}: ${price.output.toFixed(2)} / 1M
                          </>
                        )}
                      </span>
                    </span>
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-blue-600">
                  ${(costs[m.id] ?? 0).toFixed(2)}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-700">
                  {times[m.id] ? `${times[m.id].toFixed(1)}s` : '—'}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-700">
                  {calls[m.id] ?? 0}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-700">
                  {toK(tokens[m.id]?.input ?? 0)}
                  <span className="text-slate-400"> ↓ / </span>
                  {toK(tokens[m.id]?.output ?? 0)}
                  <span className="text-slate-400"> ↑ / </span>
                  {toK(tokens[m.id]?.thinking ?? 0)}
                  <Brain
                    className="ml-1 inline h-3.5 w-3.5 align-text-bottom text-slate-400"
                    aria-label={t('notice.cat.thinking')}
                  />
                  <span className="sr-only"> {t('notice.cat.thinking')}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
        {showTotal && (
          <tfoot>
            <tr className="border-t-2 border-slate-300 font-semibold [&>td]:border-r-2 [&>td]:border-slate-300 [&>td:last-child]:border-r-0">
              <td className="w-56 px-4 py-2.5 text-center text-slate-900">
                {st('notice.cat.total')}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-blue-600">
                ${total.cost.toFixed(2)}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-slate-700">
                {avgTime ? `${avgTime.toFixed(1)}s` : '—'}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-slate-700">
                {total.calls}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-slate-700">
                {toK(total.input)}
                <span className="text-slate-400"> ↓ / </span>
                {toK(total.output)}
                <span className="text-slate-400"> ↑ / </span>
                {toK(total.thinking)}
                <Brain
                  className="ml-1 inline h-3.5 w-3.5 align-text-bottom text-slate-400"
                  aria-label={t('notice.cat.thinking')}
                />
                <span className="sr-only"> {t('notice.cat.thinking')}</span>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
